import {
  METEOAPI_BASE_PATHS,
  METEOMAPS_API_BASE_PATHS,
} from "@Constants/apiBasePaths";
import { EMBED_ROOT_CLASS_SELECTOR } from "@Constants/embed";
import { OpenAPI as MeteoApiClient } from "@MeteoApi/index";
import { OpenAPI as MeteoMapsApiClient } from "@MeteoMapsApi/index";
import { Environment } from "@Models/Environment";
import { initSentry } from "@Utils/sentry";
import * as Sentry from "@sentry/react";
import React, { lazy } from "react";
import ReactDOM from "react-dom/client";
import "./App.css";

const WeatherReportApp = lazy(() =>
  import("./WeatherReportApp").then((module) => ({
    default: module.WeatherReportApp,
  })),
);
const embeds = Array.from(
  document.querySelectorAll(`.${EMBED_ROOT_CLASS_SELECTOR}`),
);
embeds
  .map((embed) => embed as HTMLElement)
  .forEach((embed) => {
    // prevent duplicated initialization by multiple scripts on the same page
    if (embed.childElementCount > 0) {
      return;
    }

    let env: Environment = "prod";
    if (embed.dataset.env && embed.dataset.env !== "") {
      env = embed.dataset.env.toLowerCase().trim() as Environment;
    }

    MeteoMapsApiClient.BASE = METEOMAPS_API_BASE_PATHS[env ?? "prod"];
    MeteoApiClient.BASE = METEOAPI_BASE_PATHS[env ?? "prod"];

    let sentryEnv: Environment = "prod";
    if (embed.dataset.sentryEnv && embed.dataset.sentryEnv !== "") {
      sentryEnv = embed.dataset.sentryEnv.toLowerCase().trim() as Environment;
    }

    const devMode = Object.prototype.hasOwnProperty.call(
      embed.dataset,
      "devmode",
    );

    // Only read Sentry DSN from environment when in devMode, aka not embedded.
    // In the case of embedded, it needs to be set via data attribute.
    let sentryDsn = devMode ? import.meta.env.VITE_FORECAST_SENTRY_DSN : "";

    // If Sentry DSN is set via data attribute, use that instead
    // eslint-disable-next-line no-prototype-builtins
    if (embed.dataset.hasOwnProperty("sentryDsn")) {
      sentryDsn = embed.dataset.sentryDsn;
    }

    const sentrySampleRate: number = +(embed.dataset.sentrySampleRate || 1.0);
    const sentryRelease = import.meta.env.VITE_RELEASE || "0000000";

    if (sentryDsn) {
      initSentry(
        sentryDsn,
        sentrySampleRate,
        sentryRelease,
        sentryEnv,
        devMode,
      );
    }

    // make root element stretch entire width
    embed.style.width = "100%";

    ReactDOM.createRoot(embed).render(
      <React.StrictMode>
        <Sentry.ErrorBoundary>
          <WeatherReportApp devMode={devMode} />
        </Sentry.ErrorBoundary>
      </React.StrictMode>,
    );
  });
